import type { App, ComponentPublicInstance } from 'vue'
import axios from 'axios'
import type { AxiosError } from 'axios'
import { trackException } from '@/infrastructure/analytics/app-insights'
import { useNotification } from '@/composables/useNotification'

/**
 * Global error-handling layers for the Vue 3 SPA — parity with the Vue 2 shell's
 * `Scripts/vue/common/errorHandler.js`. Layer 0 (the axios interceptor) lives in
 * `infrastructure/api/interceptors/error.interceptor.ts`; this module wires the
 * remaining three:
 *
 *  - **Layer 1** — `app.config.errorHandler`: component render/lifecycle/watcher
 *    errors. Skips axios errors (already reported by Layer 0).
 *  - **Layer 2a** — `window 'error'`: uncaught runtime JS errors. Reports directly
 *    because SDK exception auto-instrumentation is disabled.
 *  - **Layer 2b** — `window 'unhandledrejection'`: unhandled promise rejections.
 *    Reports only non-axios reasons (axios already covered by Layer 0).
 *
 * Toasts are debounced (2000ms, matching Vue 2's `TOAST_DEBOUNCE_MS`) and routed to
 * the notification store via `useNotification().error`.
 */

const TOAST_DEBOUNCE_MS = 2000
let lastToastTime = 0

// Debounced bridge to the notification store. Never throws.
function showErrorToast(message: string): void {
  try {
    const now = Date.now()
    if (now - lastToastTime < TOAST_DEBOUNCE_MS) {
      return
    }
    lastToastTime = now
    useNotification().error(message)
  } catch (e) {
    if (typeof console !== 'undefined') {
      console.error('[useGlobalErrorHandler] Failed to show toast:', e)
    }
  }
}

// Human-readable message for an unhandled axios rejection (parity with Vue 2
// `getFriendlyMessage`).
function getFriendlyHttpMessage(error: AxiosError): string {
  if (!error.response) {
    // Network error, CORS, DNS failure, timeout with no response.
    return 'Unable to connect. Please check your internet connection.'
  }
  const status = error.response.status
  if (status === 400) return 'The request was invalid. Please check your input and try again.'
  if (status === 403) return 'You do not have permission to perform this action.'
  if (status === 404) return 'The requested resource was not found.'
  if (status === 408) return 'The request timed out. Please try again.'
  if (status === 429) return 'Too many requests. Please wait a moment and try again.'
  if (status >= 500) return 'A server error occurred. Please try again later.'
  return 'Something went wrong. Please try again.'
}

// Resolve a human-readable component name for telemetry context. Vue 3 exposes the
// registered name via `$options.name`; fall back to the compiler-injected `__name`.
function getComponentName(instance: ComponentPublicInstance | null): string | undefined {
  return instance?.$options?.name ?? (instance?.$?.type as { __name?: string } | undefined)?.__name
}

function isAxiosError(value: unknown): boolean {
  return Boolean((value as { isAxiosError?: boolean } | null | undefined)?.isAxiosError)
}

// --- Layer 2a: uncaught runtime JS errors ---
function onWindowError(event: ErrorEvent): void {
  // Skip resource-load errors — these fire on element targets (SCRIPT, LINK, IMG,
  // ...). Runtime JS errors fire with event.target === window.
  if (event.target && event.target !== window) {
    return
  }
  // Skip cross-origin "Script error." events — the browser withholds details
  // (event.error === null). These are noise from third-party scripts.
  if (!event.error) {
    return
  }
  // Auto-instrumentation is disabled (Q1), so we report these ourselves.
  trackException(event.error, { source: 'window' })
  showErrorToast('An unexpected error occurred.')
}

// --- Layer 2b: unhandled promise rejections ---
function onUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason: unknown = event.reason

  // Skip cancelled requests (user navigated away, AbortController).
  if (axios.isCancel(reason)) {
    event.preventDefault()
    return
  }
  // Skip 401 — the session manager handles these via renewSession().
  if ((reason as AxiosError | undefined)?.response?.status === 401) {
    event.preventDefault()
    return
  }

  // Forward to App Insights ONLY for non-axios rejections. Axios errors are already
  // reported by Layer 0, so reporting them here would duplicate every failed request.
  if (!isAxiosError(reason)) {
    trackException(reason, { source: 'unhandledrejection' })
  }

  // Friendly HTTP-status message for unhandled axios rejections; generic otherwise.
  if (isAxiosError(reason)) {
    showErrorToast(getFriendlyHttpMessage(reason as AxiosError))
  } else {
    showErrorToast('An unexpected error occurred.')
  }
  event.preventDefault()
}

/**
 * Install Layers 1, 2a, and 2b. Call once from `main.ts`, after `initTelemetry()`
 * and after `app.use(pinia)` (the toast bridge resolves the notification store lazily).
 */
export function installErrorHandlers(app: App): void {
  // --- Layer 1: Vue component errors (render, watchers, lifecycle, event handlers) ---
  app.config.errorHandler = (err, instance, info) => {
    // Skip axios errors — already reported by Layer 0 (avoids double-report).
    if (isAxiosError(err)) {
      return
    }
    trackException(err, {
      source: 'vue',
      info,
      component: getComponentName(instance as ComponentPublicInstance | null),
    })
    showErrorToast('Something went wrong. Please try again.')
  }

  window.addEventListener('error', onWindowError)
  window.addEventListener('unhandledrejection', onUnhandledRejection)
}
